import React, { useEffect, useRef, useState } from "react";

/**
 * Displays an image with a teal-colored overlay rectangle around the detected
 * receipt bounds. The user sees the same "scanner" experience used by document-
 * scanner apps. Corners have rounded handles.
 *
 * Props:
 *  - src: data URL of the original image
 *  - bounds: { x, y, width, height, imageWidth, imageHeight } | null
 *  - color: stroke color (default mint teal)
 */
export default function ReceiptDetectionOverlay({ src, bounds, color = "#22D3A0" }) {
  const wrapRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [renderedSize, setRenderedSize] = useState({ w: 0, h: 0 });

  // Resize observer to follow image rendered size
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const img = el.querySelector("img");
      if (img) setRenderedSize({ w: img.clientWidth, h: img.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [src]);

  const handleImgLoad = (e) => {
    setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
    setRenderedSize({ w: e.currentTarget.clientWidth, h: e.currentTarget.clientHeight });
  };

  // Compute overlay rect in rendered (CSS) coordinates
  let overlay = null;
  if (bounds && naturalSize && renderedSize.w > 0) {
    const sx = renderedSize.w / naturalSize.w;
    const sy = renderedSize.h / naturalSize.h;
    overlay = {
      x: bounds.x * sx,
      y: bounds.y * sy,
      width: bounds.width * sx,
      height: bounds.height * sy,
    };
  }

  return (
    <div
      ref={wrapRef}
      className="relative inline-block max-w-full"
      data-testid="receipt-detection-overlay"
    >
      <img
        src={src}
        alt="معاينة الفاتورة"
        onLoad={handleImgLoad}
        className="block max-w-full max-h-[60vh] w-auto h-auto select-none"
        draggable={false}
      />

      {overlay && (
        <svg
          className="absolute inset-0 pointer-events-none"
          width={renderedSize.w}
          height={renderedSize.h}
          viewBox={`0 0 ${renderedSize.w} ${renderedSize.h}`}
        >
          {/* Dim the area outside the detected region */}
          <defs>
            <mask id="receipt-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={overlay.x}
                y={overlay.y}
                width={overlay.width}
                height={overlay.height}
                rx="8"
                ry="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.18)" mask="url(#receipt-mask)" />

          {/* Detected rectangle border */}
          <rect
            x={overlay.x}
            y={overlay.y}
            width={overlay.width}
            height={overlay.height}
            rx="6"
            ry="6"
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeLinejoin="round"
            data-testid="detection-rect"
          />

          {/* Corner handles for visual emphasis */}
          {[
            [overlay.x, overlay.y],
            [overlay.x + overlay.width, overlay.y],
            [overlay.x, overlay.y + overlay.height],
            [overlay.x + overlay.width, overlay.y + overlay.height],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="6" fill={color} stroke="white" strokeWidth="2" />
          ))}
        </svg>
      )}
    </div>
  );
}
